/**
 * Reward Selection Drawer — manages the sidebar for choosing a free/discounted
 * promo reward item from a receive layer's eligible product list.
 *
 * Follows the promo-details-drawer initialization and state management pattern.
 * Uses document-level delegation so it works from both the full cart page and the
 * cart drawer sidebar (which renders promo groups on any page).
 */

import { disableScrolling, enableScrolling } from './utils';
import { updateCart, updateCartTotal } from './cart-drawer';
import { updateCartCount } from './cart-functions';

/** DOM reference */
const rewardSelectionDrawer = document.getElementById('js-reward-selection-drawer');

/** Internal state */
let lastFocusedElement: HTMLElement | null = null;
/** Track which promo+layer is currently loaded so we can skip redundant fetches */
let activePromoId: string | null = null;
let activeLayerId: string | null = null;
/** Which variant is currently rendered — the same layer can open read-only or interactive. */
let activeReadOnly = false;

if (rewardSelectionDrawer) {
  initRewardSelectionDrawer(rewardSelectionDrawer);
}

function initRewardSelectionDrawer(drawer: HTMLElement) {
  const closeBtn = drawer.querySelector('.js-reward-drawer-close');
  const overlay = drawer.querySelector('.reward-selection-drawer__overlay');

  // Open drawer when "CHOOSE PRODUCT →" or "Change" is clicked
  document.addEventListener('click', async (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>('.js-choose-reward-product');
    if (!btn) {
      return;
    }
    e.preventDefault();
    const promoId = btn.dataset.promoId;
    const layerId = btn.dataset.layerId;
    if (!promoId || !layerId) {
      return;
    }
    await loadAndOpen(promoId, layerId, drawer);
  });

  // Open the read-only preview when "SEE AVAILABLE PRODUCTS →" is clicked on a locked slot
  document.addEventListener('click', async (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>('.js-preview-reward-products');
    if (!btn) {
      return;
    }
    e.preventDefault();
    const promoId = btn.dataset.promoId;
    const layerId = btn.dataset.layerId;
    if (!promoId || !layerId) {
      return;
    }
    await loadAndOpen(promoId, layerId, drawer, true);
  });

  // Quantity stepper — increment
  drawer.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>('.js-reward-qty-increment');
    if (!btn) {
      return;
    }
    const list = drawer.querySelector<HTMLElement>('.js-reward-eligible-list');
    const item = btn.closest<HTMLElement>('.js-reward-eligible-item');
    if (!list || !item) {
      return;
    }
    const allowance = parseInt(list.dataset.allowance ?? '1', 10);
    const currentTotal = getTotalSelected(list);
    if (currentTotal >= allowance) {
      return;
    }
    const input = item.querySelector<HTMLInputElement>('.js-reward-qty-input');
    const decrement = item.querySelector<HTMLButtonElement>('.js-reward-qty-decrement');
    if (!input) {
      return;
    }
    input.value = String(parseInt(input.value, 10) + 1);
    if (decrement) {
      decrement.disabled = false;
    }
    updateAddButton(drawer, list, allowance);
  });

  // Quantity stepper — decrement
  drawer.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('.js-reward-qty-decrement');
    if (!btn) {
      return;
    }
    const list = drawer.querySelector<HTMLElement>('.js-reward-eligible-list');
    const item = btn.closest<HTMLElement>('.js-reward-eligible-item');
    if (!list || !item) {
      return;
    }
    const allowance = parseInt(list.dataset.allowance ?? '1', 10);
    const input = item.querySelector<HTMLInputElement>('.js-reward-qty-input');
    if (!input) {
      return;
    }
    const newQty = Math.max(0, parseInt(input.value, 10) - 1);
    input.value = String(newQty);
    btn.disabled = newQty === 0;
    updateAddButton(drawer, list, allowance);
  });

  // "ADD TO CART" — replace existing reward selection with the new one
  drawer.addEventListener('click', async (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>('.js-reward-add-to-cart');
    if (!btn || btn.hasAttribute('disabled')) {
      return;
    }
    await commitRewardSelection(drawer);
  });

  // "No thanks" — dismiss the promo (records dismissal, removes reward items, prevents re-add)
  drawer.addEventListener('click', async (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>('.js-reward-decline');
    if (!btn) {
      return;
    }
    if (!activePromoId) {
      return;
    }
    btn.setAttribute('disabled', 'true');
    try {
      const res = await fetch('/api/cart/dismiss-promo?render=normal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': getCsrfToken() },
        body: JSON.stringify({ promoId: parseInt(activePromoId, 10) }),
      });
      closeRewardSelectionDrawer();
      if (!res.ok) {
        return;
      }
      const cartData = await res.json();
      if (cartData) {
        updateCart(cartData);
        updateCartTotal(cartData);
        updateCartCount(cartData);
        window.dispatchEvent(new CustomEvent('cart:update', { detail: cartData }));
      }
    } catch (err) {
      btn.removeAttribute('disabled');
      console.error('[RewardSelectionDrawer] Error dismissing promo:', err);
    }
  });

  closeBtn?.addEventListener('click', closeRewardSelectionDrawer);
  overlay?.addEventListener('click', closeRewardSelectionDrawer);

  document.addEventListener('keyup', (e) => {
    if (e.key === 'Escape' && drawer.classList.contains('open')) {
      e.preventDefault();
      closeRewardSelectionDrawer();
    }
  });
}

// ---------------------------------------------------------------------------
// Load + open
// ---------------------------------------------------------------------------

/**
 * Fetch the SSR partial and inject it into the drawer content area.
 * `readOnly` requests the preview variant, which renders the same list with nothing selectable —
 * it carries no `js-reward-eligible-list`, so the stepper/add-button wiring below finds nothing
 * to bind and the footer stays disabled.
 */
async function fetchAndInjectContent(
  promoId: string,
  layerId: string,
  drawer: HTMLElement,
  readOnly = false,
): Promise<boolean> {
  const wrapper = drawer.querySelector<HTMLElement>('.reward-selection-drawer__content-wrapper');
  wrapper?.classList.add('is-loading');
  try {
    const res = await fetch(
      `/api/cart/reward-selection?promoId=${encodeURIComponent(promoId)}&layerId=${encodeURIComponent(layerId)}`
      + (readOnly ? '&mode=preview' : ''),
    );
    if (!res.ok) {
      throw new Error(`Failed to load reward selection: ${res.status}`);
    }
    const { html, eyebrowHtml } = await res.json();
    const content = drawer.querySelector('.js-reward-selection-content');
    if (content) {
      content.innerHTML = html;
      // The header sits outside the injected content, so its eyebrow is swapped separately.
      const eyebrow = drawer.querySelector('.js-reward-drawer-eyebrow');
      if (eyebrow && eyebrowHtml) {
        eyebrow.innerHTML = eyebrowHtml;
      }
      const list = drawer.querySelector<HTMLElement>('.js-reward-eligible-list');
      if (list) {
        updateAddButton(drawer, list, parseInt(list.dataset.allowance ?? '1', 10));
      }
      wrapper?.classList.remove('is-loading');
      return true;
    }
    wrapper?.classList.remove('is-loading');
    return false;
  } catch (err) {
    wrapper?.classList.remove('is-loading');
    throw err;
  }
}

async function loadAndOpen(promoId: string, layerId: string, drawer: HTMLElement, readOnly = false) {
  // Skip redundant fetch if the drawer is already open for this exact layer IN THIS MODE — the
  // same layer can be opened read-only from a locked slot and interactive once it unlocks, and
  // those render differently.
  if (
    drawer.classList.contains('open')
    && activePromoId === promoId
    && activeLayerId === layerId
    && activeReadOnly === readOnly
  ) {
    return;
  }

  activePromoId = promoId;
  activeLayerId = layerId;
  activeReadOnly = readOnly;

  try {
    await fetchAndInjectContent(promoId, layerId, drawer, readOnly);
    openRewardSelectionDrawer();
  } catch (err) {
    activePromoId = null;
    activeLayerId = null;
    console.error('[RewardSelectionDrawer] Error loading content:', err);
  }
}

// ---------------------------------------------------------------------------
// Open / close
// ---------------------------------------------------------------------------

export function openRewardSelectionDrawer() {
  if (!rewardSelectionDrawer) {
    return;
  }
  lastFocusedElement = document.activeElement as HTMLElement;
  rewardSelectionDrawer.classList.add('open');
  rewardSelectionDrawer.removeAttribute('aria-hidden');
  rewardSelectionDrawer.removeAttribute('inert');
  disableScrolling();
  rewardSelectionDrawer.focus({ preventScroll: true });
}

export async function closeRewardSelectionDrawer() {
  if (!rewardSelectionDrawer) {
    return;
  }
  rewardSelectionDrawer.classList.remove('open');
  rewardSelectionDrawer.setAttribute('aria-hidden', 'true');
  rewardSelectionDrawer.setAttribute('inert', '');
  enableScrolling();
  if (lastFocusedElement) {
    lastFocusedElement.focus({ preventScroll: true });
    lastFocusedElement = null;
  }
  activePromoId = null;
  activeLayerId = null;
  activeReadOnly = false;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Sum all qty inputs in the eligible list */
function getTotalSelected(list: HTMLElement): number {
  let total = 0;
  list.querySelectorAll<HTMLInputElement>('.js-reward-qty-input').forEach((input) => {
    total += parseInt(input.value, 10) || 0;
  });
  return total;
}

/** Enable/disable the ADD TO CART button based on whether anything is selected */
function updateAddButton(drawer: HTMLElement, list: HTMLElement, allowance: number) {
  const addBtn = drawer.querySelector<HTMLButtonElement>('.js-reward-add-to-cart');
  if (!addBtn) {
    return;
  }
  const total = getTotalSelected(list);
  addBtn.disabled = total === 0;

  // Disable all increment buttons and grey out unselected items when allowance is exhausted
  const atMax = total >= allowance;
  list.querySelectorAll<HTMLButtonElement>('.js-reward-qty-increment').forEach((btn) => {
    btn.disabled = atMax;
  });
  list.querySelectorAll<HTMLElement>('.js-reward-eligible-item').forEach((item) => {
    const input = item.querySelector<HTMLInputElement>('.js-reward-qty-input');
    const qty = parseInt(input?.value ?? '0', 10);
    item.classList.toggle('reward-selection__eligible-item--exhausted', atMax && qty === 0);
  });
}

/** Read the existing reward rowIds embedded in the SSR content */
function getExistingRowIds(drawer: HTMLElement): string[] {
  const rowIds: string[] = [];
  drawer.querySelectorAll<HTMLElement>('.reward-selection__existing-rows [data-row-id]').forEach((el) => {
    const rowId = el.dataset.rowId;
    if (rowId) {
      rowIds.push(rowId);
    }
  });
  return rowIds;
}

/** Delete the existing promo-source reward items for this layer from the cart */
async function removeExistingRewards(drawer: HTMLElement): Promise<any> {
  const rowIds = getExistingRowIds(drawer);
  let lastCartData: any = null;
  for (const rowId of rowIds) {
    const res = await fetch('/api/cart?render=normal', {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': getCsrfToken() },
      body: JSON.stringify({ id: rowId }),
    });
    if (!res.ok) {
      throw new Error(`Failed to remove reward item: ${res.status}`);
    }
    lastCartData = await res.json();
  }
  return lastCartData;
}

/**
 * Replace the current reward selection:
 * 1. Delete all existing promo-source items for this layer
 * 2. Post the new selection
 * 3. Close drawer and trigger cart refresh
 */
async function commitRewardSelection(drawer: HTMLElement): Promise<void> {
  const addBtn = drawer.querySelector<HTMLButtonElement>('.js-reward-add-to-cart');
  const wrapper = drawer.querySelector<HTMLElement>('.reward-selection-drawer__content-wrapper');
  if (addBtn) {
    addBtn.disabled = true;
  }
  wrapper?.classList.add('is-loading');

  try {
    // Step 1: remove existing rewards
    await removeExistingRewards(drawer);

    // Step 2: build new items from the qty steppers.
    // Pin every pick to its promo + layer (promoId/layerId) whenever we have them — which we always
    // do in this drawer (activePromoId/activeLayerId are set when it opens). Pinning routes the pick
    // into activePromotions so validate keeps it on THIS promo+layer instead of re-matching it to
    // another promo the product also qualifies for, and so the pick's promo association is maintained
    // between validate calls. This covers plain rewards, buy-Y bundle picks, and tiered rewards
    // uniformly (a tiered pick in particular must be pinned: validate echoes a pinned reward back but
    // never auto-populates one, so an unpinned tiered pick would be dropped).
    type RewardItem = { id: string; quantity: number; selectionSource: 'promo'; promoId?: number; layerId?: number };
    const items: RewardItem[] = [];
    drawer.querySelectorAll<HTMLElement>('.js-reward-eligible-item').forEach((item) => {
      const productId = item.dataset.productId;
      const input = item.querySelector<HTMLInputElement>('.js-reward-qty-input');
      const qty = parseInt(input?.value ?? '0', 10);
      if (productId && qty > 0) {
        const entry: RewardItem = { id: productId, quantity: qty, selectionSource: 'promo' };
        if (activePromoId && activeLayerId) {
          entry.promoId = Number(activePromoId);
          entry.layerId = Number(activeLayerId);
        }
        items.push(entry);
      }
    });

    let cartData: any = null;
    if (items.length > 0) {
      const addRes = await fetch('/api/cart?render=normal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': getCsrfToken() },
        body: JSON.stringify({ items }),
      });
      if (!addRes.ok) {
        throw new Error(`Failed to add reward items: ${addRes.status}`);
      }
      cartData = await addRes.json();
    }

    wrapper?.classList.remove('is-loading');
    closeRewardSelectionDrawer();
    if (cartData) {
      updateCart(cartData);
      updateCartTotal(cartData);
      updateCartCount(cartData);
      window.dispatchEvent(new CustomEvent('cart:update', { detail: cartData }));
    }
  } catch (err) {
    wrapper?.classList.remove('is-loading');
    console.error('[RewardSelectionDrawer] Error committing selection:', err);
    if (addBtn) {
      addBtn.disabled = false;
    }
  }
}

function getCsrfToken(): string {
  return (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ?? '';
}
